
import lang from '@/lang'
import FIELDTYPES from '@/modules/taxForms/components/metafieldtype'
import DATATYPES from '@/modules/taxForms/components/datatypes'
import { TRANSFER_OBJECT_TYPE } from '@/modules/taxForms/constants'
import getSummary from '@/modules/taxForms/_helper/getSummary'

const getTaxFormXmlExportTransferObject = (formValues, calculationResult, calculationOptions, optData = {}) => {
  if (!formValues || !calculationResult?.formData) {
    return null
  }

  const formData = getFormMetaDataWithFormValues(calculationResult.formData, formValues)
  const summary = getSummary(formValues, calculationResult?.summaryMetaData)

  return {
    parser: calculationResult.parser,
    calculationOptions,
    summary,
    formData,
  }
}

const getTaxFormReportTransferObject = (formValues, calculationResult, calculationOptions, optData = {}) => {
  if (!calculationResult || !formValues || !calculationResult?.formData || !calculationOptions) {
    return null
  }

  const { taxForms: { forms } } = lang
  const taxFormLang = forms[calculationOptions.taxFormKey] || {}

  const formMetaData = getFormMetaDataWithFormValues(calculationResult.formData, formValues)
  const reportFormData = transformFormValuesToReportData(formMetaData)
  const summary = getSummaryWithCaptions(formValues, calculationResult.summaryMetaData, taxFormLang)
  addTaxSettingsToSummary(summary, optData.taxSettings, taxFormLang)

  return {
    calculationOptions,
    title: taxFormLang.headline,
    final: optData.final || false,
    summary,
    columnDefinitions: calculationResult.columnDefinitions,
    formData: reportFormData,
  }
}

const getTaxFormXmlAndReportExportTransferObject = (formValues, calculationResult, calculationOptions, optData = {}) => {
  const xmlExportData = getTaxFormXmlExportTransferObject(formValues, calculationResult, calculationOptions, optData)
  const reportData = getTaxFormReportTransferObject(formValues, calculationResult, calculationOptions, optData)

  return {
    ...xmlExportData,
    report: {
      final: reportData.final,
      summary: reportData.summary,
      formData: reportData.formData,
    },
  }
}

/**
 * get only changed form meta data lines
 * @param {import('@/modules/taxForms/taxFormTypes').FormLine[]} formMetaData
 * @param {Record<string, number | string>} formValues
 * @returns {FormLine[]}
 */
const getFormMetaDataWithFormValues = (formMetaData, formValues) => {
  const formValueKeys = Object.keys(formValues)
  const onlyChangedMetaData = formMetaData.filter(lines => lines.inputs.some(input => formValueKeys.includes(input.binding)))
  const copyFormMetaData = JSON.parse(JSON.stringify(onlyChangedMetaData))
  copyFormMetaData.forEach(elm => {
    if (elm.inputs) {
      elm.inputs.forEach(inp => {
        inp.value = 0
        if (inp.binding != null && formValues[inp.binding] != null) {
          inp.value = formValues[inp.binding]
        }
      })
    }
  })

  return copyFormMetaData
}

const transformFormValuesToReportData = (formMetaData) => {
  return formMetaData.flatMap(elm => {
    if (!elm.inputs) {
      return null
    }

    return elm.inputs.flatMap((input, index, list) => {
      if (!input.elsterKz || ![DATATYPES.CURRENCY, DATATYPES.CURRENCY_WITHOUT_CENT].includes(input.dataTypInfo?.dataTyp)) {
        return null
      }

      let amount = 0
      let taxAmount = 0
      if (list.length === 2) {
        amount = index === 0 ? input.value : 0
        taxAmount = index === 1 ? input.value : 0
        const nextElm = list[index + 1] || null
        if (nextElm?.type === FIELDTYPES.CUSTOM) {
          taxAmount = nextElm.value || 0
        }
      } else {
        if (input.columnIndex === 2) {
          taxAmount = input.value
        } else {
          amount = input.value
        }
      }

      // if (amount === 0 && taxAmount === 0) {
      //   return null
      // }

      return {
        linenumber: elm.linenumber,
        text: elm.text,
        elsterKz: input.elsterKz,
        amount,
        taxAmount,
      }
    })
  }).filter(Boolean)
}

const getSummaryWithCaptions = (formValues, summaryMetaData, taxFormLang) => {
  return Object.entries(getSummary(formValues, summaryMetaData))
    .reduce((target, [prop, value]) => {
      const caption = taxFormLang?.summaryCaptions?.[prop] || null
      if (caption != null) {
        target[prop] = { caption, value }
      }
      return target
    }, {})
}

const addTaxSettingsToSummary = (summary, taxSettings, taxFormLang) => {
  if (!summary) {
    return null
  }
  if (!taxSettings) {
    return summary
  }

  Object.entries(taxSettings).forEach(([prop, value]) => {
    const caption = taxFormLang?.summaryCaptions?.[prop] || null
    if (caption != null) {
      summary[prop] = { caption, value }
    }
  })

  return summary
}

// entry point
const getTransferObject = (type, formValues, calculationResult, calculationOptions, optData = {}) => {
  switch (type) {
    case TRANSFER_OBJECT_TYPE.EXPORT_XML:
      return getTaxFormXmlExportTransferObject(formValues, calculationResult, calculationOptions, optData)
    case TRANSFER_OBJECT_TYPE.REPORT_ONLY:
      return getTaxFormReportTransferObject(formValues, calculationResult, calculationOptions, optData)
    case TRANSFER_OBJECT_TYPE.EXPORT_XML_AND_REPORT:
      return getTaxFormXmlAndReportExportTransferObject(formValues, calculationResult, calculationOptions, optData)
  }

  return null
}

export default getTransferObject
